import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { keyBy } from 'lodash'
import { useReselector, useTimeoutRef } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'

import { UPLOAD_COPY, TRANSACTIONS_TABLE_DESCRIPTION } from './copyConstants'
import { getUserNeedsClarificationTransactionsSelector } from './transactions.selectors'
import { fetchUserTransactions } from './transactions.slice'
import { Badge, Tabs, Select, DatePicker, FilterToggle, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  selectTransactionFilters,
  updateTransactionFilters,
} from '@/reducers/finances/transactionFiltersReducer'
import { UploadButton, UploadSuccessPayload } from './Upload/Module'
import { UploadMessage } from './Upload/UploadMessage'
import TransactionsTable from './transaction-table'

import { UndoTransactionContextProvider } from './UndoTransactionContext'
import { FreeTrialUnreviewedAlert } from '@/components/FreeTrialUnreviewedAlert'
import { selectIsInUnservicedFreeTrial } from '@/selectors/user.selectors'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import TransactionsOnboardingCard from './TransactionsOnboardingCard'

const TAB_INDEXES: { [key: number]: string } = {
  0: 'all',
  1: 'review',
  2: 'business',
  3: 'personal',
}

type TableViews = {
  value: number
  text: string
  type: keyof typeof TRANSACTIONS_TABLE_DESCRIPTION
}[]

export const TransactionsPanel = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)
  const needsClarificationTransactions = useReselector(
    getUserNeedsClarificationTransactionsSelector
  )
  const needsReviewLength = needsClarificationTransactions.length
  const tableViews: TableViews = [
    { value: 0, text: 'All', type: 'all' },
    {
      value: 1,
      text:
        isMobile && needsReviewLength > 0
          ? `Needs Review (${needsReviewLength})`
          : 'Needs Review',
      type: 'review',
    },
    { value: 2, text: 'Business', type: 'business' },
    { value: 3, text: 'Personal', type: 'personal' },
  ]

  const freeTrialTableViews: TableViews = [
    { value: 0, text: 'All', type: 'all' },
    { value: 1, text: 'Business', type: 'business' },
    { value: 2, text: 'Personal', type: 'personal' },
  ]
  const tableViewsMap = isInUnservicedFreeTrial
    ? keyBy(freeTrialTableViews, 'type')
    : keyBy(tableViews, 'type')
  const [fetching, setFetching] = useState(true)
  const { type } = useParams<{ type: string | undefined }>()
  const [activeIndex, setActiveIndex] = useState(() => {
    switch (type) {
      case tableViewsMap.review?.type:
        return tableViewsMap.review?.value
      case tableViewsMap.business.type:
        return tableViewsMap.business.value
      case tableViewsMap.personal.type:
        return tableViewsMap.personal.value
      default:
        return tableViewsMap.all.value
    }
  })
  const bannerTimeoutRef = useTimeoutRef()
  const [completeBanner, setCompleteBanner] = useState<{
    success: boolean
    message: string
  }>()

  // Date filter + filter toggle state (shared across tabs)
  const transactionFilters = useReselector(selectTransactionFilters)
  const {
    startDate: filterStartDate,
    endDate: filterEndDate,
    showFilters,
  } = transactionFilters

  const handleShowFiltersChange = useCallback(
    (val: boolean) => {
      dispatch(updateTransactionFilters({ showFilters: val }))
    },
    [dispatch]
  )

  const handleDateRangeChange = useCallback(
    ([startDate, endDate]: [string, string]) => {
      dispatch(updateTransactionFilters({ startDate, endDate }))
    },
    [dispatch]
  )

  useEffect(() => {
    async function batchFetch() {
      await Promise.all([
        dispatch(fetchUserDocuments()),
        dispatch(fetchUserTransactions()),
      ])
      setFetching(false)
    }
    batchFetch()
  }, [dispatch, setFetching])

  const onUploadComplete = useCallback(
    ({
      success,
      data,
    }: {
      success: boolean
      data?: UploadSuccessPayload[]
    }) => {
      let message

      if (success) {
        message =
          data?.length === 1
            ? UPLOAD_COPY.SINGLE_TRANSACTION_SUCCESS_MESSAGE
            : UPLOAD_COPY.MULTIPLE_TRANSACTIONS_SUCCESS_MESSAGE
      } else {
        message = UPLOAD_COPY.SINGLE_TRANSACTION_ERROR_MESSAGE
      }
      setCompleteBanner({ success, message })

      bannerTimeoutRef.current = setTimeout(
        () => setCompleteBanner(undefined),
        5500
      )
    },
    [bannerTimeoutRef]
  )

  const updateTab = (value: string | undefined) => {
    if (value === undefined) {
      // Handle the case where the value might be undefined,
      // though with the current Select setup (not clearable, always has a value),
      // this might not be reached.
      return
    }
    const newIndex = parseInt(value, 10)
    if (isNaN(newIndex)) {
      // Handle potential parsing errors, though unlikely with current options.
      return
    }
    const newTab = TAB_INDEXES[newIndex]
    setActiveIndex(newIndex)
    navigate(`/transactions/${newTab}`, { replace: true })
  }
  const transactionTablePanes = useMemo(() => {
    if (isInUnservicedFreeTrial) {
      return [
        {
          menuItem: tableViewsMap.all.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              hideDateFilterRow
              tableType={tableViewsMap.all.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.business.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              hideDateFilterRow
              tableType={tableViewsMap.business.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.personal.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              hideDateFilterRow
              tableType={tableViewsMap.personal.type}
            />
          ),
        },
      ]
    } else {
      return [
        {
          menuItem: tableViewsMap.all.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              hideDateFilterRow
              tableType={tableViewsMap.all.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.review.text,
          ornament:
            needsReviewLength > 0 ? (
              <Badge color="red">{needsReviewLength}</Badge>
            ) : null,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              hideDateFilterRow
              tableType={tableViewsMap.review.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.business.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              hideDateFilterRow
              tableType={tableViewsMap.business.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.personal.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              hideDateFilterRow
              tableType={tableViewsMap.personal.type}
            />
          ),
        },
      ]
    }
  }, [isInUnservicedFreeTrial, tableViewsMap, fetching, needsReviewLength])

  return (
    <UndoTransactionContextProvider>
      <div id="transactionsPanel" className="sm:mx-2.5">
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between w-full mb-2">
            <Text
              variant={isMobile ? TypographyStyles.H2 : TypographyStyles.H1}
            >
              Transactions
            </Text>
            <div className="shrink-0">
              <UploadButton onUploadComplete={onUploadComplete} />
            </div>
          </div>

          {completeBanner && (
            <UploadMessage
              success={completeBanner.success}
              content={completeBanner.message}
              fadeAfterMS={5000}
            />
          )}

          <FreeTrialUnreviewedAlert />
          <TransactionsOnboardingCard />
        </div>

        {isMobile && (
          <>
            <Select
              value={String(activeIndex)}
              options={tableViews.map((view) => ({
                text: view.text,
                value: String(view.value),
              }))}
              onValueChange={updateTab}
            />
            <TransactionsTable
              fetching={fetching}
              filterable
              tableType={tableViews[activeIndex].type}
            />
          </>
        )}
        {!isMobile && (
          <div className="mt-10">
            <Tabs
              value={transactionTablePanes[activeIndex]?.menuItem}
              onValueChange={(value) => {
                const newIndex = transactionTablePanes.findIndex(
                  (pane) => pane.menuItem === value
                )
                if (newIndex !== -1) {
                  updateTab(String(newIndex))
                }
              }}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                <Tabs.List className="flex flex-wrap md:flex-nowrap gap-2 w-fit">
                  {transactionTablePanes.map((pane) => (
                    <Tabs.Trigger key={pane.menuItem} value={pane.menuItem}>
                      {pane.menuItem}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>

                <div className="flex items-center gap-2 w-full lg:w-auto">
                  <FilterToggle
                    pressed={showFilters ?? false}
                    onPressedChange={handleShowFiltersChange}
                  >
                    Filters
                  </FilterToggle>
                  <div className="flex-1 lg:flex-none lg:w-[250px]">
                    <DatePicker
                      value={
                        !filterStartDate && !filterEndDate
                          ? ['', '']
                          : [filterStartDate || '', filterEndDate || '']
                      }
                      onChange={handleDateRangeChange}
                      startDate={filterStartDate || undefined}
                      endDate={filterEndDate || undefined}
                      selectsRange
                      clearable
                      fullWidth
                      placeholder="Filter by date"
                      portalId="root"
                    />
                  </div>
                </div>
              </div>

              {transactionTablePanes.map((pane) => (
                <Tabs.Content key={pane.menuItem} value={pane.menuItem}>
                  {pane.render()}
                </Tabs.Content>
              ))}
            </Tabs>
          </div>
        )}
      </div>
    </UndoTransactionContextProvider>
  )
}
